import React, { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export interface BacktestSpikeEvent {
  start_time: string;
  end_time: string;
  peak_price_mwh: number;
  duration_hours: number;
  savings_usd: number;
  avoided_cost_usd: number;
}

export interface BacktestChartPoint {
  timestamp: string;
  price_per_mwh: number;
  is_spike: boolean;
}

export interface BacktestResult {
  id: number;
  region: string;
  run_at: string;
  months_back: number;
  records_used: number;
  is_synthetic_data: boolean;
  cluster_mw_capacity: number;
  threshold_price: number;
  baseline_cost_usd: number;
  optimized_cost_usd: number;
  gross_savings_usd: number;
  eloplus_fee_usd: number;
  net_customer_savings_usd: number;
  percentage_saved: number;
  hours_paused: number;
  total_hours: number;
  grid_curtailment_percent: number;
  spike_events: BacktestSpikeEvent[];
  chart_series: BacktestChartPoint[];
}

interface BacktestChartProps {
  result: BacktestResult;
  defaultCompanyName?: string;
}

const cardStyle: React.CSSProperties = {
  background: "#13213A",
  border: "1px solid #1F355B",
  borderRadius: 14,
  padding: "18px 20px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.24)",
};

const statCardStyle: React.CSSProperties = {
  ...cardStyle,
  padding: "14px 16px",
  display: "grid",
  gap: 6,
};

const TEAL: [number, number, number] = [14, 165, 160];
const RED: [number, number, number] = [239, 68, 68];
const PALE_RED: [number, number, number] = [254, 226, 226];
const GREEN: [number, number, number] = [34, 197, 94];

function money(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface SpikeRun {
  x1: string;
  x2: string;
}

function computeSpikeRuns(series: BacktestChartPoint[]): SpikeRun[] {
  const runs: SpikeRun[] = [];
  let runStartTimestamp: string | null = null;

  series.forEach((point, i) => {
    if (point.is_spike && runStartTimestamp === null) {
      runStartTimestamp = point.timestamp;
    }
    const isLastPoint = i === series.length - 1;
    const runEndsHere = runStartTimestamp !== null && (!point.is_spike || isLastPoint);
    if (runEndsHere) {
      const endTimestamp = point.is_spike ? point.timestamp : series[i - 1].timestamp;
      runs.push({ x1: runStartTimestamp as string, x2: endTimestamp });
      runStartTimestamp = null;
    }
  });

  return runs;
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: string;
}) {
  return (
    <div style={statCardStyle}>
      <div style={{ fontSize: 12, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color: accent || "#F8FAFC" }}>{value}</div>
    </div>
  );
}

/** Draws a simple price line + spike/pause shading directly with jsPDF vector
 * primitives (no chart-image rasterization library in this project's deps). */
function drawPdfPriceChart(
  doc: jsPDF,
  series: BacktestChartPoint[],
  threshold: number,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  if (series.length < 2) {
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text("Not enough data points to render a chart.", x, y + height / 2);
    return;
  }

  const prices = series.map((p) => p.price_per_mwh);
  const minPrice = Math.min(...prices, threshold);
  const maxPrice = Math.max(...prices, threshold) * 1.05;
  const range = maxPrice - minPrice || 1;

  const toX = (i: number) => x + (i / (series.length - 1)) * width;
  const toY = (price: number) => y + height - ((price - minPrice) / range) * height;

  doc.setDrawColor(148, 163, 184);
  doc.setLineWidth(0.75);
  doc.rect(x, y, width, height);

  // Red-tinted spike bands, with a green "paused" strip beneath the axis for
  // the same windows — same two colors used on the on-screen chart.
  const runs = computeSpikeRuns(series);
  const indexByTimestamp = new Map(series.map((p, i) => [p.timestamp, i]));
  runs.forEach((run) => {
    const startIdx = indexByTimestamp.get(run.x1) ?? 0;
    const endIdx = indexByTimestamp.get(run.x2) ?? startIdx;
    const bandX1 = toX(startIdx);
    const bandX2 = toX(endIdx);
    const bandWidth = Math.max(1, bandX2 - bandX1);

    doc.setFillColor(...PALE_RED);
    doc.rect(bandX1, y, bandWidth, height, "F");

    doc.setFillColor(...GREEN);
    doc.rect(bandX1, y + height + 2, bandWidth, 4, "F");
  });

  // Threshold reference line.
  doc.setDrawColor(...RED);
  doc.setLineDashPattern([2, 2], 0);
  const thresholdY = toY(threshold);
  doc.line(x, thresholdY, x + width, thresholdY);
  doc.setLineDashPattern([], 0);

  // Price line, drawn last so it sits above the shading.
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(1.1);
  for (let i = 0; i < series.length - 1; i++) {
    doc.line(toX(i), toY(prices[i]), toX(i + 1), toY(prices[i + 1]));
  }

  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text(`$${maxPrice.toFixed(0)}`, x - 2, y + 3, { align: "right" });
  doc.text(`$${minPrice.toFixed(0)}`, x - 2, y + height, { align: "right" });
  doc.text(formatTimestamp(series[0].timestamp), x, y + height + 12);
  doc.text(formatTimestamp(series[series.length - 1].timestamp), x + width, y + height + 12, { align: "right" });
}

export default function BacktestChart({ result, defaultCompanyName }: BacktestChartProps) {
  const [preparedFor, setPreparedFor] = useState(defaultCompanyName || "");
  const [exporting, setExporting] = useState(false);

  const chartData = useMemo(
    () =>
      result.chart_series.map((p) => ({
        ...p,
        label: formatTimestamp(p.timestamp),
      })),
    [result.chart_series]
  );

  const spikeRuns = useMemo(() => computeSpikeRuns(result.chart_series), [result.chart_series]);

  const dateRangeLabel = useMemo(() => {
    if (result.chart_series.length === 0) return "";
    const first = formatTimestamp(result.chart_series[0].timestamp);
    const last = formatTimestamp(result.chart_series[result.chart_series.length - 1].timestamp);
    return `${first} – ${last}`;
  }, [result.chart_series]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const margin = 40;
      const pageW = doc.internal.pageSize.getWidth();

      const headerH = 88;
      doc.setFillColor(...TEAL);
      doc.rect(0, 0, pageW, headerH, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(26);
      doc.text("EloPlus.AI", margin, 52);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text(`Confidential — Prepared for ${preparedFor || "[Company]"}`, margin, 72);

      doc.setTextColor(30, 41, 59);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("Savings Analysis", margin, headerH + 36);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      let coverY = headerH + 58;
      doc.text(`Region: ${result.region}`, margin, coverY);
      coverY += 18;
      doc.setFontSize(11);
      doc.setTextColor(71, 85, 105);
      doc.text(`Date range: ${dateRangeLabel}`, margin, coverY);
      coverY += 16;
      doc.text(
        `Cluster capacity: ${result.cluster_mw_capacity.toFixed(0)} MW · Threshold: $${result.threshold_price.toFixed(0)}/MWh`,
        margin,
        coverY
      );
      coverY += 16;
      if (result.is_synthetic_data) {
        doc.setTextColor(180, 83, 9);
        doc.text(
          "Note: based on simulated price data (no real ISO history available for this region/period).",
          margin,
          coverY
        );
        coverY += 16;
      }

      autoTable(doc, {
        startY: coverY + 12,
        margin: { left: margin, right: margin },
        head: [["Metric", "Value"]],
        body: [
          ["Gross Savings", money(result.gross_savings_usd)],
          ["EloPlus Fee (17.5%)", money(result.eloplus_fee_usd)],
          ["Net to You", money(result.net_customer_savings_usd)],
          ["% of Baseline Cost Saved", `${result.percentage_saved.toFixed(1)}%`],
          ["Uptime Impact (curtailment)", `${result.grid_curtailment_percent.toFixed(2)}%`],
          ["Hours Paused", `${result.hours_paused.toFixed(1)} of ${result.total_hours.toFixed(0)}`],
          ["Baseline Cost (no optimization)", money(result.baseline_cost_usd)],
          ["Optimized Cost", money(result.optimized_cost_usd)],
        ],
        styles: { fontSize: 10, cellPadding: 6 },
        headStyles: { fillColor: [15, 94, 93] },
      });

      let nextY =
        (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? coverY + 200;
      nextY += 26;

      doc.setTextColor(30, 41, 59);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("Price History & Spike Windows", margin, nextY);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(71, 85, 105);
      doc.text("Red = price above threshold  ·  Green = workload paused  ·  Teal = market price", margin, nextY + 12);

      const chartTop = nextY + 22;
      const chartHeight = 150;
      drawPdfPriceChart(doc, result.chart_series, result.threshold_price, margin, chartTop, pageW - margin * 2, chartHeight);

      let afterChartY = chartTop + chartHeight + 30;

      doc.setTextColor(30, 41, 59);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("Top 10 Worst Spike Events", margin, afterChartY);

      autoTable(doc, {
        startY: afterChartY + 10,
        margin: { left: margin, right: margin },
        head: [["Start", "End", "Peak $/MWh", "Duration (h)", "Savings"]],
        body: result.spike_events.map((evt) => [
          new Date(evt.start_time).toLocaleString(),
          new Date(evt.end_time).toLocaleString(),
          `$${evt.peak_price_mwh.toFixed(2)}`,
          evt.duration_hours.toFixed(2),
          money(evt.savings_usd),
        ]),
        styles: { fontSize: 9, cellPadding: 5 },
        headStyles: { fillColor: [30, 64, 175] },
      });

      doc.save(`EloPlus_Backtest_${result.region}_${new Date(result.run_at).toISOString().slice(0, 10)}.pdf`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
        }}
      >
        <StatCard label="Gross Savings" value={money(result.gross_savings_usd)} accent="#2DD4BF" />
        <StatCard label="EloPlus Fee (17.5%)" value={money(result.eloplus_fee_usd)} accent="#FCA5A5" />
        <StatCard label="Net to You" value={money(result.net_customer_savings_usd)} accent="#6EE7B7" />
        <StatCard label="Uptime Impact" value={`${result.grid_curtailment_percent.toFixed(2)}%`} />
      </div>

      {result.is_synthetic_data && (
        <div
          style={{
            background: "#3F2A0B",
            border: "1px solid #92400E",
            borderRadius: 10,
            padding: "10px 14px",
            color: "#FCD34D",
            fontSize: 13,
          }}
        >
          This analysis is based on simulated price data — no real ISO/EIA history was available for{" "}
          {result.region} over this period. Treat these figures as illustrative, not historical fact.
        </div>
      )}

      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: "#F8FAFC" }}>
            {result.region} Price History — {dateRangeLabel}
          </h2>
          <div style={{ fontSize: 12, color: "#94A3B8" }}>
            <span style={{ color: "#EF4444" }}>■</span> Spike (price &gt; threshold) &nbsp;
            <span style={{ color: "#22C55E" }}>■</span> Paused
          </div>
        </div>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <AreaChart data={chartData}>
              <CartesianGrid stroke="#243B63" strokeDasharray="4 4" />
              <XAxis dataKey="timestamp" tickFormatter={formatTimestamp} stroke="#94A3B8" minTickGap={60} />
              <YAxis stroke="#94A3B8" />
              <Tooltip
                labelFormatter={(value) => formatTimestamp(String(value))}
                formatter={(value) => [`$${Number(value).toFixed(2)} /MWh`, "Price"]}
                contentStyle={{
                  background: "#0F1E35",
                  border: "1px solid #1F355B",
                  borderRadius: 10,
                  color: "#E2E8F0",
                }}
              />
              <ReferenceLine
                y={result.threshold_price}
                stroke="#EF4444"
                strokeDasharray="6 4"
                label={{
                  value: `Threshold $${result.threshold_price}/MWh`,
                  fill: "#FCA5A5",
                  position: "insideTopRight",
                }}
              />
              {spikeRuns.map((run, i) => (
                <ReferenceArea
                  key={`spike-${i}`}
                  x1={run.x1}
                  x2={run.x2}
                  fill="#EF4444"
                  fillOpacity={0.15}
                  stroke="none"
                  ifOverflow="extendDomain"
                />
              ))}
              {spikeRuns.map((run, i) => (
                <ReferenceArea
                  key={`paused-${i}`}
                  x1={run.x1}
                  x2={run.x2}
                  y1={0}
                  y2={0}
                  fill="none"
                  stroke="#22C55E"
                  strokeWidth={3}
                  ifOverflow="extendDomain"
                />
              ))}
              <Area
                type="monotone"
                dataKey="price_per_mwh"
                stroke="#0EA5A0"
                fill="#0EA5A0"
                fillOpacity={0.25}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={{ margin: "0 0 14px", fontSize: 18, color: "#F8FAFC" }}>Top 10 Worst Spike Events</h2>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "#94A3B8", borderBottom: "1px solid #1F355B" }}>
                <th style={{ padding: "8px 10px" }}>Start</th>
                <th style={{ padding: "8px 10px" }}>End</th>
                <th style={{ padding: "8px 10px" }}>Peak $/MWh</th>
                <th style={{ padding: "8px 10px" }}>Duration (h)</th>
                <th style={{ padding: "8px 10px" }}>Savings</th>
              </tr>
            </thead>
            <tbody>
              {result.spike_events.map((evt, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #1F355B" }}>
                  <td style={{ padding: "8px 10px", color: "#E2E8F0" }}>{new Date(evt.start_time).toLocaleString()}</td>
                  <td style={{ padding: "8px 10px", color: "#E2E8F0" }}>{new Date(evt.end_time).toLocaleString()}</td>
                  <td style={{ padding: "8px 10px", color: "#FCA5A5", fontWeight: 700 }}>
                    ${evt.peak_price_mwh.toFixed(2)}
                  </td>
                  <td style={{ padding: "8px 10px", color: "#E2E8F0" }}>{evt.duration_hours.toFixed(2)}</td>
                  <td style={{ padding: "8px 10px", color: "#6EE7B7", fontWeight: 700 }}>
                    {money(evt.savings_usd)}
                  </td>
                </tr>
              ))}
              {result.spike_events.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: "12px 10px", color: "#94A3B8" }}>
                    No spike events above threshold in this window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ ...cardStyle, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <label style={{ display: "grid", gap: 4, flex: "1 1 260px" }}>
          <span style={{ fontSize: 12, color: "#94A3B8" }}>Prepared for (shown on the PDF cover)</span>
          <input
            type="text"
            value={preparedFor}
            onChange={(e) => setPreparedFor(e.target.value)}
            placeholder="[Company]"
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid #334155",
              background: "#0B1220",
              color: "#F8FAFC",
            }}
          />
        </label>
        <button
          type="button"
          onClick={() => void exportPdf()}
          disabled={exporting}
          style={{
            background: "#1D4ED8",
            color: "#EFF6FF",
            border: "1px solid #3B82F6",
            borderRadius: 10,
            padding: "10px 18px",
            fontWeight: 800,
            cursor: exporting ? "wait" : "pointer",
            opacity: exporting ? 0.75 : 1,
          }}
        >
          {exporting ? "Generating…" : "Export PDF"}
        </button>
      </div>
    </div>
  );
}
